import { useCallbackRef } from '@chakra-ui/react';
import isFunction from 'lodash/isFunction';
import { useEffect, useRef } from 'react';
import videojs from 'video.js';

import { defaultPlayerOptionsProps } from '@/constants/video';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { IVideoJSProps } from '@/hooks/videojs/type';
import { extractPlayerAttributes } from '@/hooks/videojs/videojs-utils';

export const useVideoJs = ({
  onReady,
  onPlay,
  onResume,
  onEnded,
  onSourceChange,
  onPause,
  onDispose,
  onTextTracksChange,
  onTouchStart,
  options,
  crossOrigin,
}: IVideoJSProps) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const onTextTracksChangeRef = useCallbackRef(onTextTracksChange);
  const { t } = useQueryParams<{ t: string }>({
    t: StringParam,
  });

  useEffect(() => {
    const finalOptions = { ...defaultPlayerOptionsProps, ...options };

    if (!playerRef.current) {
      const videoElement = document.createElement('video-js');

      videoElement.classList.add('vjs-big-play-centered');
      videoRef.current.appendChild(videoElement);

      const player = (playerRef.current = videojs(videoElement, finalOptions, () => {
        onReady && onReady(player);
      }));

      if (crossOrigin) player.crossOrigin(crossOrigin);

      if (t) {
        const parsedT = formatTParam(t);

        player.currentTime(parsedT);
      }
    } else {
      const player = playerRef.current;

      player.autoplay(finalOptions.autoplay);
      player.src(finalOptions.sources);
    }
  }, [options, videoRef]);

  useEffect(() => {
    const player = playerRef.current;

    if (t) {
      const parsedT = formatTParam(t);

      player.currentTime(parsedT);
    }
  }, [t]);

  useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player?.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  // next 3 useEffects are copied from the old VideoJS.tsx
  useEffect(() => {
    const player = playerRef.current;

    const tracks = player.textTracks();
    const newTracksOptions = options.tracks;

    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];

      const newTrackOptions = newTracksOptions.find((trackOptions) => trackOptions.id === track.id);

      // in case track is a segment-metadata
      if (newTrackOptions) {
        track.mode = newTrackOptions.mode;
      }
    }
  }, [options.tracks]);

  // Add listeners
  useEffect(() => {
    const player = playerRef.current;

    if (!player || !onTextTracksChangeRef) {
      return;
    }

    const textTracks = player.remoteTextTracks();

    function trackChangeHandler() {
      onTextTracksChangeRef(this);
    }

    textTracks.addEventListener('change', trackChangeHandler);

    return () => {
      textTracks.removeEventListener('change', trackChangeHandler);
    };
  }, [onTextTracksChangeRef]);

  useEffect(() => {
    const player = playerRef.current;

    if (player?.hlsQualitySelector && isFunction(player?.hlsQualitySelector)) {
      player.hlsQualitySelector({
        displayCurrentQuality: true,
      });
    }

    const onVideoPlay = (props) => {
      // it should be 0 logically but in some cases when you're just starting a video
      // player.currentTime() equals 0.00152 or similar small number for some reason
      if (player.currentTime() < 0.1) {
        onPlay?.(props);
      } else {
        onResume?.(props);
      }
    };

    const onVideoEnded = (props) => {
      onEnded?.(props);
    };

    const onSourceSet = (props) => {
      onSourceChange?.(props);
    };

    const onVideoPause = (props) => {
      const { totalLength, position } = extractPlayerAttributes(player);

      // Do not send event if Pause is triggered because of Complete event
      if (position === totalLength) {
        return;
      }
      onPause?.(props);
    };

    const onVideoDispose = (props) => {
      if (player.currentTime() !== 0) {
        onDispose?.(props);
      }
    };

    const onVideoTouchStart = (props) => {
      const isPaused = player.paused();

      if (isPaused) {
        player.play();
      } else {
        player.pause();
      }
      onTouchStart?.(props);
    };

    player.on('sourceset', onSourceSet);
    player.on('play', onVideoPlay);
    player.on('ended', onVideoEnded);
    player.on('pause', onVideoPause);
    player.on('dispose', onVideoDispose);
    player.on('touchstart', onVideoTouchStart);

    return () => {
      player.off('sourceset', onSourceSet);
      player.off('play', onVideoPlay);
      player.off('ended', onVideoEnded);
      player.off('pause', onVideoPause);
      player.off('dispose', onVideoDispose);
      player.off('touchstart', onVideoTouchStart);
    };
  }, [onResume, onEnded, onSourceChange, onDispose, onPause, onPlay, onTouchStart]);

  return videoRef;
};

function formatTParam(t: string) {
  const isNumber = /^\d+$/.test(t);

  if (!isNumber) return 0;
  return parseInt(t);
}
